import {Tooltip} from '@dropbox/dig-components/tooltips';
import {UIIcon} from '@dropbox/dig-icons';
import {InfoLine} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import {CURRENT_QUARTER, CURRENT_YEAR} from 'constant';
import {t} from 'i18next';
import type {ReactNode} from 'react';
import {Fragment} from 'react';

import styles from './MetricForecastGrid.module.css';

export const METRIC_FORECAST_PERIODS = ['Q1', 'Q2', 'Q3', 'Q4', 'EOY'] as const;
export type MetricForecastPeriod = (typeof METRIC_FORECAST_PERIODS)[number];

export const METRIC_FORECAST_FIELDS: ReadonlyArray<{
  field: 'target' | 'budget' | 'actual';
  label: () => string;
}> = [
  {field: 'actual', label: () => t('actual').toString()},
  {field: 'budget', label: () => t('budget').toString()},
  {field: 'target', label: () => t('target').toString()},
];

export type MetricForecastField = (typeof METRIC_FORECAST_FIELDS)[number]['field'];

export const getMetricForecastPeriod = (year: number, period: MetricForecastPeriod) =>
  `${year}${period}`;

/** Whether a metric forecast period has fully ended and can accept an actual value. */
export const hasMetricForecastPeriodEnded = (year: number, period: MetricForecastPeriod) => {
  if (year < CURRENT_YEAR) {
    return true;
  }

  if (year > CURRENT_YEAR || period === 'EOY') {
    return false;
  }

  return Number(period.slice(1)) < CURRENT_QUARTER;
};

const MetricValuesTooltip = () => (
  <div className={styles.valuesTooltip}>
    <div>
      <strong>{t('actual').toString()}</strong>
      {' — '}
      {t('metric_values_actual_tooltip').toString()}
    </div>
    <div>
      <strong>{t('budget').toString()}</strong>
      {' — '}
      {t('metric_values_budget_tooltip').toString()}
    </div>
    <div>
      <strong>{t('target').toString()}</strong>
      {' — '}
      {t('metric_values_target_tooltip').toString()}
    </div>
  </div>
);

export const MetricForecastHeader = ({
  className,
  spacerClassName,
  showValuesTooltip = false,
}: {
  className?: string;
  spacerClassName?: string;
  showValuesTooltip?: boolean;
}) => (
  <div className={cx(styles.header, className)}>
    <div
      className={cx(
        styles.headerSpacer,
        spacerClassName,
        showValuesTooltip && styles.headerSpacerWithTooltip
      )}
      aria-hidden={!showValuesTooltip}
    >
      {showValuesTooltip && (
        <>
          <span>{t('values').toString()}</span>
          <Tooltip title={<MetricValuesTooltip />} placement="top">
            <span
              className={styles.headerInfoIcon}
              aria-label="Metric values definitions"
              role="img"
              tabIndex={0}
            >
              <UIIcon src={InfoLine} size="small" />
            </span>
          </Tooltip>
        </>
      )}
    </div>
    {METRIC_FORECAST_PERIODS.map((period) => (
      <span key={`period-${period}`}>{period}</span>
    ))}
  </div>
);

export const MetricForecastGrid = ({
  year,
  className,
  rowClassName,
  labelClassName,
  renderCell,
}: {
  year: number;
  className?: string;
  rowClassName?: string;
  labelClassName?: string;
  renderCell: (args: {
    field: MetricForecastField;
    period: string;
    periodId: MetricForecastPeriod;
  }) => ReactNode;
}) => (
  <div className={cx(styles.grid, className)}>
    {METRIC_FORECAST_FIELDS.map(({field, label}) => (
      <div key={field} className={cx(styles.row, rowClassName)}>
        <div className={cx(styles.rowLabel, labelClassName)}>{label()}</div>
        {METRIC_FORECAST_PERIODS.map((periodId) => {
          const period = getMetricForecastPeriod(year, periodId);
          return (
            <Fragment key={`${field}-${period}`}>{renderCell({field, period, periodId})}</Fragment>
          );
        })}
      </div>
    ))}
  </div>
);
